import type { MantineColor } from "@mantine/core";
import {
  getItemTypeLabel,
  getOrderStatusStep,
  getProductTeeth,
  getTeethLabel,
  isRemovableItemType,
  OrderStatus,
  type Order,
  type Product,
} from "@jasper/shared";
import { getOrderStatusPresentation } from "/@/pages/dashboard/orderStatus";

export const ICON_STROKE = 1.75;

type OrderStatusBadge = { label: string; color: MantineColor };

const DESIGN_TO_REVIEW_BADGE: OrderStatusBadge = {
  label: "Design à valider",
  color: "amber",
};

export const getOrderStatusBadge = (
  order: Order,
  isAwaitingDentistReview: boolean
): OrderStatusBadge =>
  isAwaitingDentistReview
    ? DESIGN_TO_REVIEW_BADGE
    : getOrderStatusPresentation(`${order.status}`);

export const getOrderStatusDateLabel = (order: Order): string | null => {
  const step = getOrderStatusStep(order.status);

  if (step === getOrderStatusStep(OrderStatus.DRAFT)) {
    return null;
  }

  const enteredStepAt = (order.history ?? [])
    .filter(
      history => history.status && getOrderStatusStep(history.status) === step
    )
    .map(history => history.createdAt)
    .sort((a, b) => a - b)[0];
  return enteredStepAt ? `le ${formatDayMonth(new Date(enteredStepAt))}` : null;
};

// A removable product can hold one item per arch
const getHeadlineTeeth = (product: Product): number[] =>
  isRemovableItemType(product.productType)
    ? (product.removableItem ?? []).flatMap(item => item.teeth ?? [])
    : getProductTeeth(product);

export const getProductHeadline = (product: Product): string => {
  const typeLabel = getItemTypeLabel(product.productType);
  const teeth = getHeadlineTeeth(product);
  return teeth.length > 0
    ? `${typeLabel} · ${getTeethLabel(product.productType, teeth)}`
    : typeLabel;
};

export const getProductsCountLabel = (products: Product[]): string =>
  `${products.length} ${products.length > 1 ? "produits" : "produit"}`;

export const getFileExtension = (fileName: string): string =>
  fileName.includes(".") ? (fileName.split(".").pop() ?? "").toUpperCase() : "";

export const formatDayMonth = (date: Date): string => {
  const isCurrentYear = date.getFullYear() === new Date().getFullYear();

  return date.toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "long",
    ...(isCurrentYear ? {} : { year: "numeric" }),
  });
};
